import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate } from 'react-router-dom'
import { useNotify, useTranslate, Create, SimpleForm, required, TextInput } from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import { useQuery } from 'react-query'
import { CardCommon } from 'src/components/uiElements'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { supplierServices } from 'src/services/reservation/supplier/supplier.service'
import { currencyServices } from 'src/services/reservation/currency/currency.service'
import { supplierConciliationServices } from 'src/services/purchases/supplier_conciliation/supplierConciliation.service'
import { clientServices } from 'src/services/reservation/client/client.service'
import { getConciliationTypes } from './logic/conciliationTypeHelper'

const SupplierConciliationFormFields = ({
  clientQuery,
  currencyQuery,
  selectedFile,
  setSelectedFile,
  handleFileChange,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  isDragging,
  isSubmitting,
  onCancel,
}) => {
  const translate = useTranslate()
  const { control, setValue } = useFormContext()
  const clientId = useWatch({ control, name: 'client_id' })
  const [supplierList, setSupplierList] = useState([])
  const [loadingSupplierList, setLoadingSupplierList] = useState(false)
  const prevClientIdRef = useRef(clientId)

  useEffect(() => {
    if (prevClientIdRef.current !== clientId) {
      prevClientIdRef.current = clientId
      setValue('supplier_id', null)
      setSupplierList([])
    }
  }, [clientId, setValue])

  const getSuppliers = async (name) => {
    try {
      setLoadingSupplierList(true)
      const rawClientId = typeof clientId === 'object' ? clientId?.id : clientId
      const params = {}
      if (name) params.name = name
      if (rawClientId) {
        params.client_id = Number(rawClientId)
      }
      const list = await supplierServices.getByParam(params)
      setSupplierList(list || [])
    } catch (error) {
      setSupplierList([])
    } finally {
      setLoadingSupplierList(false)
    }
  }

  return (
    <Box display='flex' flexDirection='column' gap={3} p={1} width='100%'>
      {/* Card 1: Configuration */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant='h6' gutterBottom fontWeight='bold'>
          {translate('ra.title.configuration')}
        </Typography>
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size='small'>
              <InputLabel>{translate('ra.field.client')}</InputLabel>
              <SelectInput
                label={translate('ra.field.client')}
                source='client_id'
                options={clientQuery.data || []}
                optionsText={['displayName']}
                optionValue='id'
                showSearch
                style={{ width: '100%' }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AutocompleteInput
              showLabel={true}
              label={`${translate('ra.field.supplier')} *`}
              useOptionValue={true}
              source='supplier_id'
              options={supplierList}
              loading={loadingSupplierList}
              optionsText={['name']}
              onSearch={getSuppliers}
              validate={[required()]}
              resetOnLoad={false}
              style={{ width: '100%' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size='small'>
              <InputLabel>{`${translate('ra.field.type')} *`}</InputLabel>
              <SelectInput
                label={`${translate('ra.field.type')} *`}
                source='conciliation_type'
                options={getConciliationTypes(translate)}
                optionsText={['name']}
                optionValue='id'
                validate={[required()]}
                style={{ width: '100%' }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size='small'>
              <InputLabel>{`${translate('ra.field.currency')} *`}</InputLabel>
              <SelectInput
                label={`${translate('ra.field.currency')} *`}
                source='currency_id'
                options={currencyQuery.data || []}
                optionsText={['short_name']}
                optionValue='id'
                validate={[required()]}
                style={{ width: '100%' }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextInput
              label={translate('ra.field.name')}
              source='name'
              size='small'
              fullWidth
              style={{ width: '100%' }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Card 2: XLS File Upload */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography variant='h6' fontWeight='bold'>
            {translate('ra.field.xls_xlsx_file')} *
          </Typography>
          <Typography variant='caption' color='textSecondary'>
            {translate('ra.field.required_columns_info')}
          </Typography>
        </Box>

        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: '2px dashed',
            borderColor: isDragging ? 'primary.main' : '#ccc',
            borderRadius: 3,
            p: 4,
            textAlign: 'center',
            backgroundColor: isDragging ? '#f0f7ff' : '#fafafa',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant='body1' gutterBottom>
            {translate('ra.field.drag_file_here')}
          </Typography>
          <Box mt={1} mb={2}>
            <Button variant='contained' component='label' color='primary' size='small'>
              {translate('ra.field.select_file_button')}
              <input
                type='file'
                hidden
                accept='.xls,.xlsx'
                onChange={handleFileChange}
              />
            </Button>
          </Box>
          {selectedFile && (
            <Box display='flex' justifyContent='center' alignItems='center' mt={2}>
              <Chip
                icon={<InsertDriveFileIcon />}
                label={`${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(2)} MB)`}
                onDelete={() => setSelectedFile(null)}
                deleteIcon={<CloseIcon />}
                color='primary'
                variant='outlined'
              />
            </Box>
          )}
          <Typography variant='caption' display='block' color='textSecondary' mt={2}>
            {translate('ra.field.file_format_info')}
          </Typography>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box display='flex' justifyContent='flex-end' gap={2} mt={2}>
        <Button
          variant='outlined'
          color='primary'
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {translate('ra.action.cancel')}
        </Button>
        <Button
          variant='contained'
          color='primary'
          type='submit'
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color='inherit' /> : null}
        >
          {translate('ra.action.reconcile')}
        </Button>
      </Box>
    </Box>
  )
}

export const SupplierConciliationCreate = (props) => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const notify = useNotify()

  const [selectedFile, setSelectedFile] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const currencyQuery = useQuery('currency-all', () => currencyServices.getByAll())
  const clientQuery = useQuery('reservation-client-all', async () => {
    const res = await clientServices.getByAll()
    const list = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : [])
    return list.map((c) => ({
      ...c,
      displayName: c.long_name || c.short_name || `Cliente #${c.id}`,
    }))
  })

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      validateAndSetFile(file)
    }
  }

  const validateAndSetFile = (file) => {
    const validExtensions = ['.xls', '.xlsx']
    const fileName = file.name.toLowerCase()
    const isValidExt = validExtensions.some((ext) => fileName.endsWith(ext))

    if (!isValidExt) {
      notify(translate('ra.field.file_format_info') || 'Invalid format. Use .xls o .xlsx', { type: 'warning' })
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      notify('El archivo supera los 10 MB', { type: 'warning' })
      return
    }

    setSelectedFile(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0])
    }
  }

  const handleSubmit = async (formData) => {
    const supplierId =
      typeof formData.supplier_id === 'object'
        ? formData.supplier_id?.id
        : formData.supplier_id
    const clientId =
      typeof formData.client_id === 'object'
        ? formData.client_id?.id
        : formData.client_id
    const currencyId =
      typeof formData.currency_id === 'object'
        ? formData.currency_id?.id
        : formData.currency_id
    const conciliationType =
      typeof formData.conciliation_type === 'object'
        ? formData.conciliation_type?.id
        : formData.conciliation_type
    const name = formData.name ? String(formData.name).trim() : null

    if (!supplierId) {
      notify(
        translate('ra.message.please_select_field', {
          field: translate('ra.field.supplier'),
        }),
        { type: 'warning' }
      )
      return
    }
    if (!conciliationType) {
      notify(
        translate('ra.message.please_select_field', {
          field: translate('ra.field.type'),
        }),
        { type: 'warning' }
      )
      return
    }
    if (!currencyId) {
      notify(
        translate('ra.message.please_select_field', {
          field: translate('ra.field.currency'),
        }),
        { type: 'warning' }
      )
      return
    }
    if (!selectedFile) {
      notify(
        translate('ra.message.please_select_field', {
          field: translate('ra.field.xls_xlsx_file'),
        }),
        { type: 'warning' }
      )
      return
    }

    try {
      setIsSubmitting(true)
      const res = await supplierConciliationServices.processFile(
        selectedFile,
        Number(supplierId),
        Number(currencyId),
        conciliationType || null,
        clientId ? Number(clientId) : null,
        name || null
      )
      const conciliationId = res?.id
      notify(
        translate('ra.notification.conciliation_process_started', {
          id: conciliationId ? `${conciliationId}` : '',
        }),
        { type: 'info' }
      )
      navigate('/supplier-conciliation')
    } catch (error) {
      notify('ra.notification.conciliation_process_error', { type: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Create {...props} component='div'>
      <Container className='content' maxWidth='lg'>
        <CardCommon title={translate('ra.title.create_supplier_conciliation')}>
          <SimpleForm toolbar={null} onSubmit={handleSubmit}>
            <SupplierConciliationFormFields
              clientQuery={clientQuery}
              currencyQuery={currencyQuery}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              handleFileChange={handleFileChange}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              isDragging={isDragging}
              isSubmitting={isSubmitting}
              onCancel={() => navigate('/supplier-conciliation')}
            />
          </SimpleForm>
        </CardCommon>
      </Container>
    </Create>
  )
}

